import { addNumberOfReferences, createNavItem, NavItem } from '@app/pcs/shared/components/sidenav/nav-item';
import { BASE_COMPOSITE_RELATIONS_PATH } from '@dossier/models/composite-relation';
import { DossierAclActionMap } from '@dossier/reducers/basic-data/basic-data-mapper';
import * as R from 'ramda';

export enum DossierSegment {
  BasicData = 'basicData',
  Comments = 'comments',
  ApplicantTimetable = 'applicantTimetable',
  ImTimetable = 'imTimetable',
  CossTimetable = 'cossTimetable',
  FeasibilityStudyTimetable = 'feasibilityStudyTimetable',
  TrainInformation = 'trainInformation',
  TrainComposition = 'trainComposition',
  Connections = 'connections',
  Links = 'links',
  CompositeRelations = 'compositeRelations',
  Attachments = 'attachments',
  Control = 'control',
  PapRequests = 'pap-requests',
  ViewHistory = 'view-history'
}

export interface DossierView {
  id: DossierSegment;
  label: string;
  route: string[];
  icon: string;
  aclAction?: string;
}

export const dossierViews: DossierView[] = [
  {
    id: DossierSegment.BasicData,
    label: 'label.dossier.basic.data',
    route: ['basic-data'],
    icon: 'chat'
  },
  {
    id: DossierSegment.Comments,
    label: 'general.label.comments',
    route: ['comments'],
    icon: 'forum'
  },
  {
    id: DossierSegment.ApplicantTimetable,
    label: 'label.dossier.timetable.applicant',
    route: ['geography', 'timetable', 'APPLICANT'],
    icon: 'train',
    aclAction: DossierAclActionMap.ViewApplicantTimetable
  },
  {
    id: DossierSegment.ImTimetable,
    label: 'label.dossier.timetable.im',
    route: ['geography', 'timetable', 'IM'],
    icon: 'pcs-icon-train_tracks',
    aclAction: DossierAclActionMap.ViewImTimetable
  },
  {
    id: DossierSegment.CossTimetable,
    label: 'label.dossier.timetable.coss',
    route: ['geography', 'timetable', 'COSS'],
    icon: 'call_split',
    aclAction: DossierAclActionMap.ViewCossTimetable
  },
  {
    id: DossierSegment.FeasibilityStudyTimetable,
    label: 'timetable.fs.title',
    route: ['geography', 'timetable', 'FS'],
    icon: 'pcs-icon-feasibility-study',
    aclAction: DossierAclActionMap.ViewFeasibilityStudyTimetable
  },
  {
    id: DossierSegment.TrainInformation,
    label: 'general.label.train.information',
    route: ['train-information'],
    icon: 'info'
  },
  {
    id: DossierSegment.TrainComposition,
    label: 'general.label.train.composition',
    route: ['train-composition'],
    icon: 'timeline',
    aclAction: DossierAclActionMap.ViewTrainComposition
  },
  {
    id: DossierSegment.Connections,
    label: 'general.label.connections',
    route: ['connections'],
    icon: 'share',
    aclAction: DossierAclActionMap.ViewConnections
  },
  {
    id: DossierSegment.Links,
    label: 'general.label.dossier.links',
    route: ['links'],
    icon: 'link'
  },
  {
    id: DossierSegment.CompositeRelations,
    label: 'general.label.dossier.composite.relations',
    route: [BASE_COMPOSITE_RELATIONS_PATH],
    icon: 'merge_type',
    aclAction: DossierAclActionMap.ViewCompositeRelations
  },
  {
    id: DossierSegment.Control,
    label: 'general.label.control',
    route: ['control'],
    icon: 'linear_scale',
    aclAction: DossierAclActionMap.ViewControl
  },
  {
    id: DossierSegment.PapRequests,
    label: 'papRequestDetails.module.title',
    route: ['pap-requests'],
    icon: 'pcs-icon-link_off',
    aclAction: DossierAclActionMap.ViewPapRequestDetails
  },
  {
    id: DossierSegment.ViewHistory,
    label: 'dossierArchive.sectionTitle',
    route: ['view-history'],
    icon: 'update'
  }
];

const getDossierViewById = (viewKey: string) => <DossierView>R.find(R.propEq('id', viewKey), dossierViews);
type GetRoute = (d: DossierView) => string[];
const getRoute: GetRoute = R.prop('route');
type GetAclAction = (d: DossierView) => string;
const getAclAction: GetAclAction = R.prop('aclAction');

const createDossierViewSegment = (dossierSegment: DossierSegment, numberOfReferencesPerDossierSegment?: Record<string, number>) => {
  const view: DossierView = getDossierViewById(dossierSegment);
  const aclAction = getAclAction(view);
  const navItem = aclAction
    ? createNavItem({path: view.route, label: view.label, icon: view.icon, aclAction})
    : createNavItem({path: view.route, label: view.label, icon: view.icon});
  return numberOfReferencesPerDossierSegment
    ? addNumberOfReferences(navItem, numberOfReferencesPerDossierSegment[dossierSegment])
    : navItem;
};

export const resolveLinkRoute = (viewKey: string) => {
  const dossierView: DossierView = getDossierViewById(viewKey);
  const basicDataView: DossierView = getDossierViewById(DossierSegment.BasicData);
  return getRoute(dossierView) || getRoute(basicDataView);
};

export const dossierViewSegments = (numberOfReferencesPerDossierSegment?: Record<string, number>): NavItem[][] => [
  [
    createDossierViewSegment(DossierSegment.BasicData),
    createDossierViewSegment(DossierSegment.Control),
    createDossierViewSegment(DossierSegment.Comments, numberOfReferencesPerDossierSegment),
  ],
  [
    createDossierViewSegment(DossierSegment.ApplicantTimetable),
    createDossierViewSegment(DossierSegment.ImTimetable),
    createDossierViewSegment(DossierSegment.CossTimetable),
    createDossierViewSegment(DossierSegment.FeasibilityStudyTimetable),
    createDossierViewSegment(DossierSegment.PapRequests),
  ],
  [
    createDossierViewSegment(DossierSegment.TrainInformation),
    createDossierViewSegment(DossierSegment.TrainComposition),
    createDossierViewSegment(DossierSegment.Connections, numberOfReferencesPerDossierSegment),
    createDossierViewSegment(DossierSegment.Links, numberOfReferencesPerDossierSegment),
    createDossierViewSegment(DossierSegment.CompositeRelations, numberOfReferencesPerDossierSegment),
    // TODO VV: add it when attachmetns will be implemented
    // createDossierViewSegment(DossierSegments.Attachments, numberOfReferencesPerDossierSegment)
  ],
  [
    createDossierViewSegment(DossierSegment.ViewHistory)
  ]
];

type GetNumberOfReferencesPerDossierSegment = (references: number[]) => Record<string, number>;
const getNumberOfReferencesPerDossierSegment: GetNumberOfReferencesPerDossierSegment = ([commentsLength, linksLength, connectionsLength, compositeRelationsLength]: number[]) => ({
  [DossierSegment.Comments]: commentsLength,
  [DossierSegment.Links]: linksLength,
  [DossierSegment.Connections]: connectionsLength,
  [DossierSegment.CompositeRelations]: compositeRelationsLength
});

type DossierSegmentsWithNumberOfReferences = (references: number[]) => NavItem[][];
export const dossierSegmentsWithNumberOfReferences = <DossierSegmentsWithNumberOfReferences>R.compose(
  dossierViewSegments,
  getNumberOfReferencesPerDossierSegment
);
